import { startTransition, useEffect, useMemo, useState, type FormEvent } from "react"

import { FleetB2BPageShell } from "@/features/operator/fleet/fleet-b2b-page-shell"
import {
  applyFleetOnboardingRouteContext,
  FLEET_ONBOARDING_MODE_LABELS,
  FLEET_VERTICAL_LABELS,
  type FleetOnboardingSearch,
  useFleetOnboardingDraft,
} from "@/features/operator/fleet/fleet-onboarding-shared"
import {
  getOperatorSessionOrganization,
  canAccessFleetSuperAdminControls,
  titleizeToken,
} from "@/features/operator/shared/operator-access"
import {
  useOptionalOperatorShellSession,
} from "@/features/operator/shared/operator-shell-session-context"
import {
  OperatorEmptyState,
  OperatorPanel,
  OperatorStatusMessage,
} from "@/features/operator/shared/operator-shell"
import {
  apiClient,
  type FleetPilotLeadRecord,
  isApiError,
  type FleetCustomerProvisioningResponse,
  type FleetOnboardingRunRecord,
  type OperatorOrganizationRecord,
} from "@/lib/api/client"

interface FleetEntryPageProps {
  onOpenRun: (input: {
    mode?: "import" | "manual"
    organizationId: string
    runId: string
    step: "import" | "manual" | "organization" | "overview" | "review"
    vertical: "intercity_bus" | "trucking" | "two_wheeler_delivery"
  }) => void
  onOpenSetup: (input: {
    mode?: "import" | "manual"
    organizationId: string
    runId: string
    vertical: "intercity_bus" | "trucking" | "two_wheeler_delivery"
  }) => void
  search: FleetOnboardingSearch
}

function resolveRunRouteStep(run: FleetOnboardingRunRecord) {
  if (run.currentStep === "import") {
    return "import"
  }

  if (run.currentStep === "manual") {
    return "manual"
  }

  if (run.currentStep === "review") {
    return "review"
  }

  if (run.currentStep === "organization") {
    return "organization"
  }

  return "organization"
}

function describeRunStatus(status: FleetOnboardingRunRecord["status"]) {
  switch (status) {
    case "provisioned":
      return "Provisioned by super admin"
    case "invited":
      return "Fleet owner invite sent"
    case "first_login_pending":
      return "Waiting for first login"
    case "onboarding_complete":
      return "Onboarding complete"
    default:
      return "Onboarding in progress"
  }
}

interface FleetCustomerProvisioningDraft {
  fleetCategory: "intercity_bus" | "trucking" | "two_wheeler_delivery"
  legalName: string
  ownerEmail: string
  ownerName: string
  ownerPhone: string
  ownerRole: "fleet_admin" | "fleet_owner"
  primaryCity: string
  primaryCountry: string
  primaryRegion: string
  organizationName: string
}

type ClientVerificationFilter = "active" | "all" | "provisioned" | "ready"

type FleetPilotLeadOutcome = "follow_up" | "provisioned" | "ready"

interface FleetPilotLeadAssessment {
  blockers: string[]
  label: string
  outcome: FleetPilotLeadOutcome
  score: number
  strengths: string[]
  summary: string
}

const INITIAL_PROVISIONING_DRAFT: FleetCustomerProvisioningDraft = {
  fleetCategory: "trucking",
  legalName: "",
  ownerEmail: "",
  ownerName: "",
  ownerPhone: "",
  ownerRole: "fleet_owner",
  primaryCity: "",
  primaryCountry: "India",
  primaryRegion: "",
  organizationName: "",
}

const CLIENT_VERIFICATION_FILTER_OPTIONS: Array<{
  label: string
  value: ClientVerificationFilter
}> = [
  { label: "Active queue", value: "active" },
  { label: "Ready", value: "ready" },
  { label: "Provisioned", value: "provisioned" },
  { label: "All", value: "all" },
]

function formatDateTime(value: string | null | undefined) {
  if (!value) {
    return "Not recorded"
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return "Not recorded"
  }

  return new Intl.DateTimeFormat("en-IN", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parsed)
}

function mapLeadSegmentToProvisioningCategory(
  fleetSegment: FleetPilotLeadRecord["fleetSegment"]
): FleetCustomerProvisioningDraft["fleetCategory"] {
  if (fleetSegment === "intercity_bus") {
    return "intercity_bus"
  }

  if (fleetSegment === "delivery_rider") {
    return "two_wheeler_delivery"
  }

  return "trucking"
}

function buildProvisioningDraftFromLead(
  lead: FleetPilotLeadRecord
): FleetCustomerProvisioningDraft {
  return {
    fleetCategory: mapLeadSegmentToProvisioningCategory(lead.fleetSegment),
    legalName: lead.companyName || "",
    ownerEmail: lead.workEmail || "",
    ownerName: lead.contactName || "",
    ownerPhone: lead.phone || "",
    ownerRole: "fleet_owner",
    primaryCity: lead.city || "",
    primaryCountry: "India",
    primaryRegion: lead.operatingRegion || "",
    organizationName: lead.companyName || "",
  }
}

function assessFleetPilotLead(lead: FleetPilotLeadRecord): FleetPilotLeadAssessment {
  const signals = [
    {
      available: Boolean(lead.contactName),
      label: "Named decision maker",
    },
    {
      available: Boolean(lead.workEmail),
      label: "Verified work email",
    },
    {
      available: Boolean(lead.phone),
      label: "Direct phone line",
    },
    {
      available: Boolean(lead.city || lead.operatingRegion),
      label: "Operating geography captured",
    },
    {
      available: Boolean(
        typeof lead.estimatedVehicleCount === "number" || lead.fleetSizeBand
      ),
      label: "Fleet size signal captured",
    },
    {
      available: Boolean(lead.rolloutTimeline || lead.preferredDemoWindow),
      label: "Follow-up timing captured",
    },
  ]
  const availableSignals = signals.filter((signal) => signal.available)
  const blockers: string[] = []

  if (!lead.city && !lead.operatingRegion) {
    blockers.push("Confirm the operating city or region before provisioning.")
  }

  if (
    typeof lead.estimatedVehicleCount !== "number"
    && !lead.fleetSizeBand
  ) {
    blockers.push("Capture the fleet size band or estimated vehicle count.")
  }

  if (!lead.rolloutTimeline && !lead.preferredDemoWindow) {
    blockers.push("Ask for a rollout timeline or a concrete demo window.")
  }

  if (!lead.currentSafetyStack) {
    blockers.push("Document the current safety stack before handoff.")
  }

  const score = Math.round((availableSignals.length / signals.length) * 100)
  const isProvisioned =
    lead.status === "provisioned" || Boolean(lead.organizationId)

  if (isProvisioned) {
    return {
      blockers: [],
      label: "Provisioned",
      outcome: "provisioned",
      score: 100,
      strengths: availableSignals.map((signal) => signal.label).slice(0, 4),
      summary: "Already linked to a Fleet customer and handed off into onboarding.",
    }
  }

  if (!blockers.length && score >= 80) {
    return {
      blockers: [],
      label: "Ready for invite",
      outcome: "ready",
      score,
      strengths: availableSignals.map((signal) => signal.label).slice(0, 4),
      summary: "The submission has enough scope, contact, and rollout detail to provision now.",
    }
  }

  return {
    blockers,
    label: "Needs qualification",
    outcome: "follow_up",
    score,
    strengths: availableSignals.map((signal) => signal.label).slice(0, 3),
    summary: "Review the missing operational signals before converting this lead into a customer.",
  }
}

function leadOutcomeClassName(outcome: FleetPilotLeadOutcome) {
  switch (outcome) {
    case "provisioned":
      return "rounded-full bg-chart-3/15 px-3 py-1 text-xs font-medium text-foreground"
    case "ready":
      return "rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary"
    default:
      return "rounded-full bg-chart-4/15 px-3 py-1 text-xs font-medium text-foreground"
  }
}

function FleetVerificationMetric({
  caption,
  title,
  value,
}: {
  caption: string
  title: string
  value: string
}) {
  return (
    <div className="rounded-[1.75rem] border border-border/70 bg-background/70 px-4 py-4">
      <div className="text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase">
        {caption}
      </div>
      <div className="mt-2 text-2xl font-semibold">{value}</div>
      <div className="mt-1 text-sm text-muted-foreground">{title}</div>
    </div>
  )
}

function FleetVerificationField({
  label,
  monospace = false,
  value,
}: {
  label: string
  monospace?: boolean
  value: string | null | undefined
}) {
  return (
    <div className="rounded-2xl border border-border/70 bg-background/65 px-4 py-4">
      <div className="text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase">
        {label}
      </div>
      <div
        className={
          monospace
            ? "mt-3 break-all font-mono text-xs leading-6 text-foreground"
            : "mt-3 text-sm leading-6 text-foreground"
        }
      >
        {value || "Not provided"}
      </div>
    </div>
  )
}

function FleetVerificationBlock({
  label,
  value,
}: {
  label: string
  value: string | null | undefined
}) {
  return (
    <div className="rounded-2xl border border-border/70 bg-background/65 px-4 py-4">
      <div className="text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase">
        {label}
      </div>
      <div className="mt-3 whitespace-pre-wrap text-sm leading-6 text-foreground">
        {value || "Not provided"}
      </div>
    </div>
  )
}

export function FleetEntryPage({ onOpenRun, onOpenSetup, search }: FleetEntryPageProps) {
  const shellSession = useOptionalOperatorShellSession()
  const { setDraft } = useFleetOnboardingDraft()
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [resolvedOrganization, setResolvedOrganization] =
    useState<OperatorOrganizationRecord | null>(null)
  const [resolvedRun, setResolvedRun] = useState<FleetOnboardingRunRecord | null>(null)
  const [provisioningDraft, setProvisioningDraft] = useState<FleetCustomerProvisioningDraft>(
    INITIAL_PROVISIONING_DRAFT
  )
  const [isProvisioningCustomer, setIsProvisioningCustomer] = useState(false)
  const [provisioningError, setProvisioningError] = useState<string | null>(null)
  const [latestProvisioning, setLatestProvisioning] =
    useState<FleetCustomerProvisioningResponse | null>(null)
  const [pilotLeads, setPilotLeads] = useState<FleetPilotLeadRecord[]>([])
  const [pilotLeadQueueError, setPilotLeadQueueError] = useState<string | null>(null)
  const [isPilotLeadQueueLoading, setIsPilotLeadQueueLoading] = useState(false)
  const [pilotLeadFilter, setPilotLeadFilter] =
    useState<ClientVerificationFilter>("active")
  const [selectedPilotLeadId, setSelectedPilotLeadId] = useState<string | null>(null)
  const [provisioningSourceLead, setProvisioningSourceLead] =
    useState<FleetPilotLeadRecord | null>(null)

  const sessionOrganizationId = useMemo(() => {
    if (shellSession?.status !== "ready") {
      return null
    }

    return getOperatorSessionOrganization(shellSession.session).id
  }, [shellSession])
  const isFleetSafetySuperAdmin = useMemo(() => {
    return shellSession?.status === "ready"
      && canAccessFleetSuperAdminControls(shellSession.session)
  }, [shellSession])

  async function submitCustomerProvisioning(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (isProvisioningCustomer) {
      return
    }

    setIsProvisioningCustomer(true)
    setProvisioningError(null)
    const sourceLead = provisioningSourceLead

    try {
      const result = await apiClient.provisionFleetCustomer({
        fleetOwner: {
          email: provisioningDraft.ownerEmail.trim(),
          name: provisioningDraft.ownerName.trim(),
          phoneNumber: provisioningDraft.ownerPhone.trim() || null,
          role: provisioningDraft.ownerRole,
        },
        organization: {
          city: provisioningDraft.primaryCity.trim(),
          country: provisioningDraft.primaryCountry.trim(),
          fleetCategory: provisioningDraft.fleetCategory,
          legalName: provisioningDraft.legalName.trim() || undefined,
          name: provisioningDraft.organizationName.trim(),
          region: provisioningDraft.primaryRegion.trim() || undefined,
          type: "fleet_partner",
        },
        organizationId: sourceLead?.organizationId || null,
        pilotLeadId: sourceLead?.id || null,
      })

      setDraft((previous) =>
        applyFleetOnboardingRouteContext(previous, {
          mode: result.onboardingRun?.onboardingMode || undefined,
          organizationId:
            result.onboardingRun?.organizationId
            || result.organization?.id
            || undefined,
          runId: result.onboardingRun?.id || undefined,
          vertical: result.onboardingRun?.vertical || undefined,
        })
      )
      const nextPilotLead =
        result.pilotLead
        || (sourceLead
          ? {
              ...sourceLead,
              organizationId:
                result.organization?.id || sourceLead.organizationId || null,
              organizationName:
                result.organization?.name || sourceLead.organizationName || null,
              status: "provisioned",
            }
          : null)

      if (nextPilotLead) {
        setPilotLeads((current) =>
          current.map((lead) => (lead.id === nextPilotLead.id ? nextPilotLead : lead))
        )
        setProvisioningSourceLead(nextPilotLead)
        setSelectedPilotLeadId(nextPilotLead.id)
      }

      setLatestProvisioning({
        ...result,
        pilotLead: nextPilotLead,
      })
    } catch (error) {
      setProvisioningError(
        isApiError(error)
          ? error.message
          : error instanceof Error
            ? error.message
            : "Failed to provision Fleet customer and owner."
      )
    } finally {
      setIsProvisioningCustomer(false)
    }
  }

  function resetProvisioningForm() {
    setProvisioningDraft(INITIAL_PROVISIONING_DRAFT)
    setProvisioningError(null)
    setLatestProvisioning(null)
    setProvisioningSourceLead(null)
  }

  function loadPilotLeadIntoProvisioning(lead: FleetPilotLeadRecord) {
    setProvisioningDraft(buildProvisioningDraftFromLead(lead))
    setProvisioningError(null)
    setLatestProvisioning(null)
    setProvisioningSourceLead(lead)
  }

  useEffect(() => {
    const organizationId = search.organizationId || sessionOrganizationId
    if (!organizationId) {
      setResolvedOrganization(null)
      setResolvedRun(null)
      setIsLoading(false)
      return
    }

    let isActive = true
    setIsLoading(true)
    setError(null)

    void apiClient
      .resolveFleetOnboardingRun({
        onboardingMode: search.mode,
        organizationId,
        runId: search.runId,
        step: search.step,
        vertical: search.vertical,
      })
      .then((result) => {
        if (!isActive) {
          return
        }

        setDraft((previous) =>
          applyFleetOnboardingRouteContext(previous, {
            mode: result.run.onboardingMode || undefined,
            organizationId: result.run.organizationId,
            runId: result.run.id,
            vertical: result.run.vertical,
          })
        )
        setResolvedOrganization(result.organization)
        setResolvedRun(result.run)
      })
      .catch((loadError) => {
        if (!isActive) {
          return
        }

        setError(
          isApiError(loadError)
            ? loadError.message
            : loadError instanceof Error
              ? loadError.message
              : "Failed to resolve the Fleet onboarding run."
        )
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false)
        }
      })

    return () => {
      isActive = false
    }
  }, [
    search.mode,
    search.organizationId,
    search.runId,
    search.step,
    search.vertical,
    sessionOrganizationId,
    setDraft,
  ])

  useEffect(() => {
    if (!isFleetSafetySuperAdmin) {
      return
    }

    let isActive = true

    async function loadPilotLeadQueue() {
      startTransition(() => {
        setIsPilotLeadQueueLoading(true)
        setPilotLeadQueueError(null)
      })

      try {
        const nextLeads = await apiClient.getFleetProvisioningLeads(24)

        if (!isActive) {
          return
        }

        startTransition(() => {
          setPilotLeads(nextLeads)
          setSelectedPilotLeadId((current) =>
            current && nextLeads.some((lead) => lead.id === current)
              ? current
              : nextLeads[0]?.id || null
          )
        })
      } catch (error) {
        if (!isActive) {
          return
        }

        startTransition(() => {
          setPilotLeads([])
          setPilotLeadQueueError(
            isApiError(error)
              ? error.message
              : error instanceof Error
                ? error.message
                : "Failed to load the Fleet client verification queue."
          )
        })
      } finally {
        if (isActive) {
          startTransition(() => {
            setIsPilotLeadQueueLoading(false)
          })
        }
      }
    }

    void loadPilotLeadQueue()

    return () => {
      isActive = false
    }
  }, [isFleetSafetySuperAdmin])

  const organizationLabel = resolvedOrganization?.name || "Fleet organization"
  const pilotLeadEntries = useMemo(
    () =>
      pilotLeads.map((lead) => ({
        assessment: assessFleetPilotLead(lead),
        lead,
      })),
    [pilotLeads]
  )
  const filteredPilotLeadEntries = useMemo(() => {
    return pilotLeadEntries.filter((entry) => {
      if (pilotLeadFilter === "all") {
        return true
      }

      if (pilotLeadFilter === "active") {
        return entry.assessment.outcome !== "provisioned"
      }

      if (pilotLeadFilter === "ready") {
        return entry.assessment.outcome === "ready"
      }

      return entry.assessment.outcome === "provisioned"
    })
  }, [pilotLeadEntries, pilotLeadFilter])
  const selectedPilotLeadEntry = useMemo(() => {
    return (
      pilotLeadEntries.find((entry) => entry.lead.id === selectedPilotLeadId)
      || filteredPilotLeadEntries[0]
      || pilotLeadEntries[0]
      || null
    )
  }, [filteredPilotLeadEntries, pilotLeadEntries, selectedPilotLeadId])
  const selectedPilotLead = selectedPilotLeadEntry?.lead || null
  const selectedPilotLeadAssessment = selectedPilotLeadEntry?.assessment || null
  const verificationMetrics = useMemo(() => {
    const activeCount = pilotLeadEntries.filter(
      (entry) => entry.assessment.outcome !== "provisioned"
    ).length
    const readyCount = pilotLeadEntries.filter(
      (entry) => entry.assessment.outcome === "ready"
    ).length
    const provisionedCount = pilotLeadEntries.filter(
      (entry) => entry.assessment.outcome === "provisioned"
    ).length

    return {
      activeCount,
      provisionedCount,
      readyCount,
      totalCount: pilotLeadEntries.length,
    }
  }, [pilotLeadEntries])

  return (
    <FleetB2BPageShell
      activeSection="overview"
      title="Fleet onboarding entry"
      description="Resolve or resume the current Fleet onboarding run, then continue through the left Fleet menu."
      highlights={[
        "Super admin provisioning and fleet owner onboarding now connect through one run record.",
        "Fleet entry resolves and resumes the active onboarding run for the current organization.",
        "Onboarding, operating units, assets, assignments, kits, and print can share the same run context.",
      ]}
    >
      <div className="grid gap-5">
        {isLoading ? (
          <OperatorStatusMessage>Resolving Fleet onboarding run…</OperatorStatusMessage>
        ) : null}

        {error ? <OperatorStatusMessage tone="danger">{error}</OperatorStatusMessage> : null}

        {isFleetSafetySuperAdmin ? (
          <>
            <div className="grid gap-5 xl:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)]">
              <OperatorPanel
                title="Client verification queue"
                description="Review Fleet Safety pilot submissions, qualify the best-fit clients, then pass them into provisioning."
              >
                <div className="grid gap-5">
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                    <FleetVerificationMetric
                      caption="Inbound"
                      title="total submissions in scope"
                      value={String(verificationMetrics.totalCount)}
                    />
                    <FleetVerificationMetric
                      caption="Active"
                      title="still awaiting provisioning"
                      value={String(verificationMetrics.activeCount)}
                    />
                    <FleetVerificationMetric
                      caption="Ready"
                      title="can be invited now"
                      value={String(verificationMetrics.readyCount)}
                    />
                    <FleetVerificationMetric
                      caption="Provisioned"
                      title="already handed into onboarding"
                      value={String(verificationMetrics.provisionedCount)}
                    />
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {CLIENT_VERIFICATION_FILTER_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={
                          pilotLeadFilter === option.value
                            ? "rounded-full bg-foreground px-4 py-2 text-sm font-medium text-background"
                            : "rounded-full border border-border bg-background px-4 py-2 text-sm font-medium text-foreground"
                        }
                        onClick={() => setPilotLeadFilter(option.value)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>

                  {pilotLeadQueueError ? (
                    <OperatorStatusMessage tone="danger">
                      {pilotLeadQueueError}
                    </OperatorStatusMessage>
                  ) : null}

                  {isPilotLeadQueueLoading ? (
                    <OperatorStatusMessage>
                      Loading the Fleet client verification queue…
                    </OperatorStatusMessage>
                  ) : null}

                  {!isPilotLeadQueueLoading && !filteredPilotLeadEntries.length ? (
                    <OperatorEmptyState
                      title="No pilot submissions match this queue."
                      message="New Fleet Safety pilot submissions from the public funnel will appear here for super-admin review."
                    />
                  ) : null}

                  {!isPilotLeadQueueLoading && filteredPilotLeadEntries.length ? (
                    <div className="grid gap-3">
                      {filteredPilotLeadEntries.map(({ assessment, lead }) => (
                        <button
                          key={lead.id}
                          type="button"
                          className={
                            selectedPilotLead?.id === lead.id
                              ? "rounded-[1.8rem] border border-primary/35 bg-primary/5 px-4 py-4 text-left"
                              : "rounded-[1.8rem] border border-border/70 bg-background/65 px-4 py-4 text-left transition-colors hover:border-primary/25 hover:bg-background/80"
                          }
                          onClick={() => setSelectedPilotLeadId(lead.id)}
                        >
                          <div className="flex flex-wrap items-start justify-between gap-3">
                            <div>
                              <p className="text-base font-semibold">
                                {lead.companyName || "Unknown company"}
                              </p>
                              <p className="mt-1 text-sm text-muted-foreground">
                                {[
                                  lead.contactName || "Contact not provided",
                                  lead.workEmail || "No email",
                                  lead.phone || "No phone",
                                ].join(" · ")}
                              </p>
                            </div>
                            <span className={leadOutcomeClassName(assessment.outcome)}>
                              {assessment.label}
                            </span>
                          </div>
                          <div className="mt-4 grid gap-3 md:grid-cols-[minmax(0,1fr)_10rem]">
                            <div>
                              <p className="text-sm leading-6 text-muted-foreground">
                                {[
                                  lead.fleetSegment
                                    ? titleizeToken(lead.fleetSegment)
                                    : "Fleet",
                                  lead.fleetSizeBand || null,
                                  lead.city || lead.operatingRegion || null,
                                ]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </p>
                              <p className="mt-2 text-sm text-muted-foreground">
                                {assessment.summary}
                              </p>
                            </div>
                            <div className="rounded-2xl border border-border/70 bg-background/75 px-3 py-3">
                              <div className="text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase">
                                Readiness
                              </div>
                              <div className="mt-2 text-2xl font-semibold">
                                {assessment.score}
                              </div>
                              <div className="text-sm text-muted-foreground">out of 100</div>
                            </div>
                          </div>
                          <p className="mt-3 text-sm text-muted-foreground">
                            Submitted {formatDateTime(lead.createdAt)}
                            {lead.organizationName ? ` · linked to ${lead.organizationName}` : ""}
                          </p>
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
              </OperatorPanel>

              <OperatorPanel
                title="Client verification detail"
                description={
                  selectedPilotLead
                    ? "Review readiness, contact quality, and rollout signals before loading the lead into provisioning."
                    : "Choose a pilot submission from the queue to inspect the client profile here."
                }
              >
                {selectedPilotLead && selectedPilotLeadAssessment ? (
                  <div className="grid gap-5">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="grid gap-1">
                        <h3 className="text-lg font-semibold">
                          {selectedPilotLead.companyName || "Unknown company"}
                        </h3>
                        <p className="text-sm leading-6 text-muted-foreground">
                          {[
                            selectedPilotLead.contactName || "Contact not provided",
                            selectedPilotLead.workEmail || "No email",
                            selectedPilotLead.phone || "No phone",
                          ].join(" · ")}
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={leadOutcomeClassName(selectedPilotLeadAssessment.outcome)}>
                          {selectedPilotLeadAssessment.label}
                        </span>
                        <span className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">
                          {titleizeToken(selectedPilotLead.status || "new")}
                        </span>
                        <span className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-secondary-foreground">
                          Notification {titleizeToken(selectedPilotLead.notificationStatus || "pending")}
                        </span>
                      </div>
                    </div>

                    <div className="grid gap-3 lg:grid-cols-[12rem_minmax(0,1fr)]">
                      <div className="rounded-[1.75rem] bg-foreground px-4 py-4 text-background">
                        <div className="text-xs font-semibold tracking-[0.18em] uppercase text-background/65">
                          Verification score
                        </div>
                        <div className="mt-3 text-4xl font-semibold">
                          {selectedPilotLeadAssessment.score}
                        </div>
                        <div className="mt-2 text-sm text-background/70">
                          {selectedPilotLeadAssessment.label}
                        </div>
                      </div>

                      <div className="rounded-[1.75rem] border border-border/70 bg-background/70 px-4 py-4">
                        <p className="text-sm leading-6 text-foreground">
                          {selectedPilotLeadAssessment.summary}
                        </p>
                        {selectedPilotLeadAssessment.strengths.length ? (
                          <div className="mt-4 flex flex-wrap gap-2">
                            {selectedPilotLeadAssessment.strengths.map((strength) => (
                              <span
                                key={strength}
                                className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary"
                              >
                                {strength}
                              </span>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </div>

                    {selectedPilotLeadAssessment.blockers.length ? (
                      <OperatorStatusMessage tone="warning">
                        {selectedPilotLeadAssessment.blockers.join(" ")}
                      </OperatorStatusMessage>
                    ) : null}

                    <div className="grid gap-3 md:grid-cols-2">
                      <FleetVerificationField
                        label="Fleet segment"
                        value={
                          selectedPilotLead.fleetSegment
                            ? titleizeToken(selectedPilotLead.fleetSegment)
                            : null
                        }
                      />
                      <FleetVerificationField
                        label="Fleet size"
                        value={selectedPilotLead.fleetSizeBand}
                      />
                      <FleetVerificationField
                        label="Estimated vehicles"
                        value={
                          typeof selectedPilotLead.estimatedVehicleCount === "number"
                            ? String(selectedPilotLead.estimatedVehicleCount)
                            : null
                        }
                      />
                      <FleetVerificationField
                        label="City"
                        value={selectedPilotLead.city}
                      />
                      <FleetVerificationField
                        label="Region"
                        value={selectedPilotLead.operatingRegion}
                      />
                      <FleetVerificationField
                        label="Source surface"
                        value={
                          selectedPilotLead.sourceSurface
                            ? titleizeToken(selectedPilotLead.sourceSurface)
                            : null
                        }
                      />
                      <FleetVerificationField
                        label="Submitted"
                        value={formatDateTime(selectedPilotLead.createdAt)}
                      />
                      <FleetVerificationField
                        label="Linked organization"
                        value={
                          selectedPilotLead.organizationName
                          || selectedPilotLead.organizationId
                          || null
                        }
                      />
                      <FleetVerificationField
                        label="Reference ID"
                        monospace
                        value={selectedPilotLead.id}
                      />
                      <FleetVerificationField
                        label="Notification updated"
                        value={formatDateTime(selectedPilotLead.notifiedAt || selectedPilotLead.updatedAt)}
                      />
                    </div>

                    <div className="grid gap-3 lg:grid-cols-2">
                      <FleetVerificationBlock
                        label="Current safety stack"
                        value={selectedPilotLead.currentSafetyStack}
                      />
                      <FleetVerificationBlock
                        label="Pilot goals"
                        value={selectedPilotLead.pilotGoals}
                      />
                      <FleetVerificationBlock
                        label="Rollout timeline"
                        value={selectedPilotLead.rolloutTimeline}
                      />
                      <FleetVerificationBlock
                        label="Preferred demo window"
                        value={selectedPilotLead.preferredDemoWindow}
                      />
                    </div>

                    <div className="flex flex-wrap gap-3">
                      <button
                        type="button"
                        disabled={provisioningSourceLead?.id === selectedPilotLead.id}
                        className="rounded-2xl bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60"
                        onClick={() => loadPilotLeadIntoProvisioning(selectedPilotLead)}
                      >
                        {provisioningSourceLead?.id === selectedPilotLead.id
                          ? "Loaded into provisioning draft"
                          : "Load into provisioning draft"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <OperatorEmptyState
                    title="No pilot submission selected."
                    message="Pick a client from the verification queue to review their readiness and hand them into provisioning."
                  />
                )}
              </OperatorPanel>
            </div>

            <OperatorPanel
              title="Fleet Safety customer provisioning"
              description="Create the Fleet customer organization and owner account, then continue in Fleet Onboarding from the left menu."
            >
              <div className="grid gap-5">
                {provisioningSourceLead ? (
                  <div className="rounded-[1.8rem] border border-primary/25 bg-primary/5 px-4 py-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="text-xs font-semibold tracking-[0.18em] text-primary uppercase">
                          Lead context attached
                        </p>
                        <p className="mt-2 text-base font-semibold">
                          {provisioningSourceLead.companyName || "Unknown company"}
                        </p>
                        <p className="mt-1 text-sm text-muted-foreground">
                          Provisioning will update the selected pilot lead and mark it as provisioned.
                        </p>
                      </div>
                      <button
                        type="button"
                        className="rounded-2xl border border-border bg-background px-4 py-2 text-sm font-medium"
                        onClick={() => setProvisioningSourceLead(null)}
                      >
                        Detach lead
                      </button>
                    </div>
                  </div>
                ) : null}

                <form className="grid gap-4" onSubmit={submitCustomerProvisioning}>
                  <div className="grid gap-3 lg:grid-cols-2">
                    <label className="grid gap-2 text-sm">
                      <span className="text-muted-foreground">Organization name</span>
                      <input
                        aria-label="Organization name"
                        className="rounded-2xl border border-border bg-background px-3 py-2"
                        value={provisioningDraft.organizationName}
                        onChange={(event) =>
                          setProvisioningDraft((previous) => ({
                            ...previous,
                            organizationName: event.target.value,
                          }))
                        }
                        placeholder="Atlas Freight India"
                        required
                      />
                    </label>
                    <label className="grid gap-2 text-sm">
                      <span className="text-muted-foreground">Legal name</span>
                      <input
                        aria-label="Legal name"
                        className="rounded-2xl border border-border bg-background px-3 py-2"
                        value={provisioningDraft.legalName}
                        onChange={(event) =>
                          setProvisioningDraft((previous) => ({
                            ...previous,
                            legalName: event.target.value,
                          }))
                        }
                        placeholder="Atlas Freight India Private Limited"
                      />
                    </label>
                    <label className="grid gap-2 text-sm">
                      <span className="text-muted-foreground">Primary city</span>
                      <input
                        aria-label="Primary city"
                        className="rounded-2xl border border-border bg-background px-3 py-2"
                        value={provisioningDraft.primaryCity}
                        onChange={(event) =>
                          setProvisioningDraft((previous) => ({
                            ...previous,
                            primaryCity: event.target.value,
                          }))
                        }
                        placeholder="Bengaluru"
                        required
                      />
                    </label>
                    <label className="grid gap-2 text-sm">
                      <span className="text-muted-foreground">Primary region</span>
                      <input
                        aria-label="Primary region"
                        className="rounded-2xl border border-border bg-background px-3 py-2"
                        value={provisioningDraft.primaryRegion}
                        onChange={(event) =>
                          setProvisioningDraft((previous) => ({
                            ...previous,
                            primaryRegion: event.target.value,
                          }))
                        }
                        placeholder="Karnataka"
                      />
                    </label>
                    <label className="grid gap-2 text-sm">
                      <span className="text-muted-foreground">Country</span>
                      <input
                        aria-label="Country"
                        className="rounded-2xl border border-border bg-background px-3 py-2"
                        value={provisioningDraft.primaryCountry}
                        onChange={(event) =>
                          setProvisioningDraft((previous) => ({
                            ...previous,
                            primaryCountry: event.target.value,
                          }))
                        }
                        required
                      />
                    </label>
                    <label className="grid gap-2 text-sm">
                      <span className="text-muted-foreground">Fleet category</span>
                      <select
                        aria-label="Fleet category"
                        className="rounded-2xl border border-border bg-background px-3 py-2"
                        value={provisioningDraft.fleetCategory}
                        onChange={(event) =>
                          setProvisioningDraft((previous) => ({
                            ...previous,
                            fleetCategory: event.target
                              .value as FleetCustomerProvisioningDraft["fleetCategory"],
                          }))
                        }
                      >
                        <option value="trucking">Trucking</option>
                        <option value="intercity_bus">Intercity bus</option>
                        <option value="two_wheeler_delivery">Two-wheeler delivery</option>
                      </select>
                    </label>
                    <label className="grid gap-2 text-sm">
                      <span className="text-muted-foreground">Fleet owner/admin name</span>
                      <input
                        aria-label="Fleet owner/admin name"
                        className="rounded-2xl border border-border bg-background px-3 py-2"
                        value={provisioningDraft.ownerName}
                        onChange={(event) =>
                          setProvisioningDraft((previous) => ({
                            ...previous,
                            ownerName: event.target.value,
                          }))
                        }
                        placeholder="Fleet operations owner"
                        required
                      />
                    </label>
                    <label className="grid gap-2 text-sm">
                      <span className="text-muted-foreground">Fleet owner/admin email</span>
                      <input
                        aria-label="Fleet owner/admin email"
                        className="rounded-2xl border border-border bg-background px-3 py-2"
                        type="email"
                        value={provisioningDraft.ownerEmail}
                        onChange={(event) =>
                          setProvisioningDraft((previous) => ({
                            ...previous,
                            ownerEmail: event.target.value,
                          }))
                        }
                        placeholder="ops@atlasfreight.com"
                        required
                      />
                    </label>
                    <label className="grid gap-2 text-sm">
                      <span className="text-muted-foreground">Fleet owner/admin phone</span>
                      <input
                        aria-label="Fleet owner/admin phone"
                        className="rounded-2xl border border-border bg-background px-3 py-2"
                        value={provisioningDraft.ownerPhone}
                        onChange={(event) =>
                          setProvisioningDraft((previous) => ({
                            ...previous,
                            ownerPhone: event.target.value,
                          }))
                        }
                        placeholder="+919900001111"
                      />
                    </label>
                    <label className="grid gap-2 text-sm">
                      <span className="text-muted-foreground">Provisioned role</span>
                      <select
                        aria-label="Provisioned role"
                        className="rounded-2xl border border-border bg-background px-3 py-2"
                        value={provisioningDraft.ownerRole}
                        onChange={(event) =>
                          setProvisioningDraft((previous) => ({
                            ...previous,
                            ownerRole: event.target
                              .value as FleetCustomerProvisioningDraft["ownerRole"],
                          }))
                        }
                      >
                        <option value="fleet_owner">Fleet owner</option>
                        <option value="fleet_admin">Fleet admin</option>
                      </select>
                    </label>
                  </div>

                  <div className="flex flex-wrap gap-3">
                    <button
                      type="submit"
                      disabled={isProvisioningCustomer}
                      className="rounded-2xl bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-60"
                    >
                      {isProvisioningCustomer
                        ? "Provisioning customer..."
                        : provisioningSourceLead
                          ? "Create customer from verified lead"
                          : "Create customer and send invite"}
                    </button>
                    <button
                      type="button"
                      onClick={resetProvisioningForm}
                      className="rounded-2xl border border-border px-4 py-2 text-sm font-medium"
                    >
                      Reset form
                    </button>
                  </div>
                </form>

                {provisioningError ? (
                  <OperatorStatusMessage tone="danger">
                    {provisioningError}
                  </OperatorStatusMessage>
                ) : null}

                {!provisioningError && !latestProvisioning ? (
                  <OperatorStatusMessage>
                    Verify a client first when possible, then provision customer access and move
                    into Fleet Onboarding from the left menu.
                  </OperatorStatusMessage>
                ) : null}

                {latestProvisioning ? (
                  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                    <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                      <div className="text-sm text-muted-foreground">Organization</div>
                      <div className="mt-1 text-base font-semibold">
                        {latestProvisioning.organization?.name || "Unknown organization"}
                      </div>
                      <div className="mt-1 text-sm text-muted-foreground">
                        {latestProvisioning.organization?.id || "No organization ID returned"}
                      </div>
                    </div>
                    <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                      <div className="text-sm text-muted-foreground">Provisioned owner account</div>
                      <div className="mt-1 text-base font-semibold">
                        {latestProvisioning.fleetOwner.name}
                      </div>
                      <div className="mt-1 text-sm text-muted-foreground">
                        {latestProvisioning.fleetOwner.email} • {latestProvisioning.fleetOwner.role}
                      </div>
                    </div>
                    <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                      <div className="text-sm text-muted-foreground">Invite delivery</div>
                      <div className="mt-1 text-base font-semibold">
                        {latestProvisioning.inviteDelivery.status}
                      </div>
                      <div className="mt-1 text-sm text-muted-foreground">
                        Run ID: {latestProvisioning.onboardingRun?.id || "Not created"}
                      </div>
                    </div>
                    <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                      <div className="text-sm text-muted-foreground">Lead handoff</div>
                      <div className="mt-1 text-base font-semibold">
                        {latestProvisioning.pilotLead?.companyName || "Manual provisioning"}
                      </div>
                      <div className="mt-1 text-sm text-muted-foreground">
                        {latestProvisioning.pilotLead
                          ? titleizeToken(latestProvisioning.pilotLead.status || "provisioned")
                          : "No source lead attached"}
                      </div>
                    </div>
                    {latestProvisioning.onboardingRun
                      ? (() => {
                          const provisionedRun = latestProvisioning.onboardingRun

                          return (
                            <button
                              type="button"
                              onClick={() =>
                                onOpenRun({
                                  mode: provisionedRun.onboardingMode || undefined,
                                  organizationId: provisionedRun.organizationId,
                                  runId: provisionedRun.id,
                                  step: resolveRunRouteStep(provisionedRun),
                                  vertical: provisionedRun.vertical,
                                })
                              }
                              className="rounded-2xl border border-border px-4 py-2 text-sm font-medium md:col-span-2 xl:col-span-4"
                            >
                              Open provisioned onboarding run
                            </button>
                          )
                        })()
                      : null}
                  </div>
                ) : null}
              </div>
            </OperatorPanel>
          </>
        ) : null}

        {!isFleetSafetySuperAdmin && !isLoading && !error && !resolvedRun ? (
          <OperatorEmptyState
            title="No Fleet run is available for this session."
            message="A fleet owner session linked to one organization is required to start or resume onboarding from Fleet entry."
          />
        ) : null}

        {resolvedRun ? (
          <>
            <OperatorStatusMessage>
              Active organization: <strong>{organizationLabel}</strong>. Current run: <strong>{resolvedRun.id}</strong>.
            </OperatorStatusMessage>

            <OperatorPanel
              title="Current onboarding run"
              description="Use this run context as you move through Onboarding, Operating Units, Assets, People, Assignments, and Kits from the left Fleet menu."
            >
              <div className="grid gap-3 md:grid-cols-2">
                  <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                    <div className="text-sm text-muted-foreground">Run status</div>
                    <div className="mt-2 text-lg font-semibold">
                      {describeRunStatus(resolvedRun.status)}
                    </div>
                  </div>
                  <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                    <div className="text-sm text-muted-foreground">Current step</div>
                    <div className="mt-2 text-lg font-semibold">
                      {resolvedRun.currentStep === "overview" ? "Organization" : resolvedRun.currentStep}
                    </div>
                  </div>
                  <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                    <div className="text-sm text-muted-foreground">Vertical</div>
                    <div className="mt-2 text-lg font-semibold">
                      {FLEET_VERTICAL_LABELS[resolvedRun.vertical]}
                    </div>
                  </div>
                  <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
                    <div className="text-sm text-muted-foreground">Onboarding mode</div>
                    <div className="mt-2 text-lg font-semibold">
                      {resolvedRun.onboardingMode
                        ? FLEET_ONBOARDING_MODE_LABELS[resolvedRun.onboardingMode]
                        : "Not selected"}
                    </div>
                  </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() =>
                    onOpenRun({
                      mode: resolvedRun.onboardingMode || undefined,
                      organizationId: resolvedRun.organizationId,
                      runId: resolvedRun.id,
                      step: resolveRunRouteStep(resolvedRun),
                      vertical: resolvedRun.vertical,
                    })
                  }
                  className="rounded-2xl bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
                >
                  Resume current onboarding run
                </button>
                <button
                  type="button"
                  onClick={() =>
                    onOpenSetup({
                      mode: resolvedRun.onboardingMode || undefined,
                      organizationId: resolvedRun.organizationId,
                      runId: resolvedRun.id,
                      vertical: resolvedRun.vertical,
                    })
                  }
                  className="rounded-2xl border border-border px-4 py-2 text-sm font-medium"
                >
                  Open onboarding setup
                </button>
              </div>
            </OperatorPanel>
          </>
        ) : null}
      </div>
    </FleetB2BPageShell>
  )
}
